import { useCallback, useRef, useState } from 'react';
import { useDictation } from './useDictation';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

export type VoiceAnswerState = 'idle' | 'recording' | 'transcribing' | 'error';

/**
 * The reusable voice-answer contract (LANE DNA-BUILD, Phase 1). Ported from
 * apps/web/src/lib/useVoiceAnswer.ts — same mime-aware recorder + upload
 * pattern (docs/design/dna-interview-port-2026-07-05.md §3, "the iOS/mp4
 * fix"), one deliberate change: does NOT pass `lang: 'en-AU'` to useDictation.
 * Throughline's own useDictation.ts already carries the no-AU-default fix
 * (its own header comment: "no hardcoded AU... derive from the target role's
 * country" per CLAUDE.md's locale rule) — porting the apps/web hardcode here
 * would silently reintroduce the exact thing that hook was written to avoid.
 *
 * Captures a spoken answer two ways at once:
 *   1. useDictation (Web Speech) gives a live interim transcript while the
 *      user talks, so the surface never feels dead.
 *   2. A MediaRecorder captures the raw audio; on stop it is uploaded to
 *      /dna/sessions/:id/transcribe, where ElevenLabs Scribe produces the
 *      authoritative transcript (accents, names, numbers). That is what we
 *      return, plus the R2 key of the stored clip.
 *
 * iOS / Safari note: MediaRecorder there emits audio/mp4, not audio/webm. We
 * read the recorder's own mimeType to type the Blob and pick the upload
 * filename extension, so the server sees a truthful content type either way.
 */
export function useVoiceAnswer(sessionId: string | null) {
  const [state, setState] = useState<VoiceAnswerState>('idle');
  const dictation = useDictation();
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRef.current = mr;
      mr.start();
      dictation.start();
      setState('recording');
    } catch {
      setState('error');
    }
  }, [dictation]);

  /** Stop recording, upload the audio, return the Scribe transcript + R2 key. */
  const stopAndTranscribe = useCallback(async (): Promise<{
    text: string;
    r2Key: string | null;
  } | null> => {
    const mr = mediaRef.current;
    dictation.stop();
    if (!mr || !sessionId) {
      setState('idle');
      return null;
    }
    setState('transcribing');

    // Resolve the recorded blob, typed by the recorder's actual mimeType so the
    // upload content type is correct on Safari (mp4) and Chrome / Firefox (webm).
    const blob: Blob = await new Promise((resolve) => {
      mr.onstop = () =>
        resolve(new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' }));
      mr.stop();
    });
    for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    streamRef.current = null;

    try {
      const form = new FormData();
      form.append('file', blob, filenameFor(blob.type));
      const res = await fetch(`${API_BASE}/dna/sessions/${sessionId}/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        setState('error');
        return null;
      }
      const data = (await res.json()) as { text: string; r2Key: string | null };
      setState('idle');
      return data;
    } catch {
      setState('error');
      return null;
    }
  }, [dictation, sessionId]);

  return {
    state,
    interim: dictation.interim,
    supported: dictation.supported,
    start,
    stopAndTranscribe,
  };
}

/** Pick an upload filename whose extension matches the recorded container, so
 *  the server and Scribe both see a truthful type. Defaults to .webm. */
function filenameFor(mimeType: string): string {
  const t = mimeType.toLowerCase();
  if (t.includes('mp4') || t.includes('m4a') || t.includes('aac')) return 'answer.mp4';
  if (t.includes('mpeg') || t.includes('mp3')) return 'answer.mp3';
  if (t.includes('ogg')) return 'answer.ogg';
  if (t.includes('wav')) return 'answer.wav';
  return 'answer.webm';
}
